/**
 * FilterAddBuilder — VQE-Edit "+ Add" trigger for the flat WHERE pill view.
 * With `pickConnector`, the picker carries an AND/OR connector toggle so one
 * button covers both joins (replaces the old separate Add AND / Add OR pair).
 *
 * ENABLED branch: delegates entirely to `VqeFilterChipBuilder mode="add"` (E4)
 * which owns the column picker (CommandPickerPopover), the DraftFilterChip, and
 * the `addFilter` commit (including analytics).
 *
 * DISABLED / no-context branch: renders a disabled `CommandPickerPopover`
 * trigger (greyed-out button + tooltip explaining why editing is unavailable).
 *
 * PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — add/modify/remove
 * filter (WHERE / HAVING).
 */

import type { VqeEditContext } from '../../edit/editContext';
import { CommandPickerPopover } from '../common/CommandPickerPopover';
import { VqeFilterChipBuilder } from '../common/VqeFilterChipBuilder';
import type { WhereCombinator } from '../../../../sql-resolver/types';
import type { BooleanClauseTarget } from '../../../../sql-resolver/types/structuralEdit';
import { vqeCopy } from '../../ux/vqeCopy';

export interface FilterAddBuilderProps {
  readonly testId: string;
  readonly triggerLabel: string;
  readonly editContext?: VqeEditContext;
  readonly disabled?: boolean;
  readonly disabledTooltip?: string;
  readonly combinator?: WhereCombinator;
  /**
   * When true, the picker shows an AND/OR connector toggle so a single "+ Add"
   * button covers both joins (Round 8 — replaces the separate Add AND / Add OR
   * buttons). `combinator` is the toggle's initial value.
   */
  readonly pickConnector?: boolean;
  /** Target clause for the `addFilter` commit — `'where'` (default) or `'having'`. */
  readonly clause?: BooleanClauseTarget;
  /** Forwarded to the builder: the draft chip opened / closed. */
  readonly onDraftOpenChange?: (open: boolean) => void;
}

export function FilterAddBuilder({
  testId,
  triggerLabel,
  editContext,
  disabled = false,
  disabledTooltip,
  combinator = 'AND',
  pickConnector = false,
  clause,
  onDraftOpenChange,
}: FilterAddBuilderProps) {
  if (!editContext || disabled) {
    // Disabled shell: greyed-out trigger + tooltip explaining why editing is
    // unavailable. CommandPickerPopover is designed for this — its `disabled`
    // prop renders the button as aria-disabled with the "why" tooltip, and
    // `groups` defaults to [] so no items are shown.
    return (
      <CommandPickerPopover
        testId={testId}
        triggerLabel={triggerLabel}
        disabled
        tooltip={disabledTooltip ?? vqeCopy.disabled.editingUnavailable}
      />
    );
  }
  return (
    <VqeFilterChipBuilder
      mode="add"
      testId={testId}
      triggerLabel={triggerLabel}
      editContext={editContext}
      combinator={combinator}
      pickConnector={pickConnector}
      clause={clause}
      onDraftOpenChange={onDraftOpenChange}
    />
  );
}
